'use strict';
/* ==========================================================================
   levels/video.js — step 1, LYING WITH EXTRA STEPS: the interactive video (shared/engine/v2/core/player.js) in the
   lesson's ZINE look (core-local/zine.js), one scene per script segment (content/script.json s1–s5; each segment's
   "visual" is the storyboard).

   Every scene is a PURE FUNCTION of the part time t: beats are keyed to the dialogue through ctx.L / ctx.E / ctx.W
   (via cues(), which turns the ±Infinity of other parts into ±1e6), so the choreography follows whatever the voice
   timing is (text timing now, the recorded clips later, with no code change) and the player can seek, replay and run
   at 25×. Branches: a reply cue is min() over the question's reply ids (branch()): in the reply part only the branch
   that plays is finite, in the lines part all are +∞ (not yet), in the after part all are −∞ (done).

   Honesty (DESIGN.md, docs/topics/facts-simile.md): every quote on screen is Devices.SCRIPT_LINES (the model's own
   table, which tools/devicecheck.js checks against the script and the facts bank: Q2 Juliet, Q4 Sonnet 130, Q13
   Dickinson's 1891 text); every device label is Devices.name / Devices.colour (Zine.label / Zine.mark). The only
   number that is not a quote: the US short ton (1 ton = 2,000 lb), the storyboard's scale prop in s3, and the facts
   file's safe wording of the 1977 estimate (fun fact row "Six metaphors a minute" → CORRECTED wording).

   Staging (like lessons/branches): each segment opens on a zine chapter card that tears away; Nia (left) and Ms. Park
   (right) stand full size ("two": captions in the top band on the speaker's side) or shrink to bottom-corner insets
   ("inset": captions in the bottom band beside them) when the pages need the screen. Portrait: the player puts the
   captions in the dock; the typed quotes and cards are written into the dock too (Dock.text, note()).
   Cost: backdrops and the linocut prints (balcony, backpack) are baked once into layers (OFF budget); paper pieces and
   the zine pages are cached sprites; the rain is ~70 plain strokes; no per-frame shadowOn().
   Art: IMG.nia / nia_point / park / park_point (via core-local/hooks.js poses), microphone_stand, spotlight, bird
   (via Zine.art: an ink stand-in until the cut-out is in). Sfx: snaps / mic_tap / typewriter when loaded, else a shared
   sound or silence (fx()).
   Debug / QA: window.SimileVideo (BODY, layers, notes: the last dock notes, marks: the reply marks drawn).
   ========================================================================== */
(function () {
  const S = Player.scenes;
  const HANDF = "'Caveat Brush'";
  const Z = () => window.Zine;
  const INK = '#141210', GREY = '#6d6a64', RED = '#d9352c', WHITE = '#fffefa';
  const LINES = () => (window.Devices && Devices.SCRIPT_LINES) || {};
  const COL = id => (window.Devices ? Devices.colour(id) : '#fff04a');
  const COVER_PAL = [['#141210', '#f7f5ef'], ['#f7f5ef', '#141210'], ['#fff04a', '#141210'], ['news', '#141210'], ['#ff6fbf', '#141210'], ['#141210', '#fff04a'], ['#72f06e', '#141210']];
  const TON_LB = 2000;   // the US short ton (the storyboard's scale prop; the script says "Two thousand pounds")

  /* ---------------- small helpers ---------------- */
  const fin = x => (x === Infinity ? 1e6 : x === -Infinity ? -1e6 : x);
  const cues = ctx => ({ L: id => fin(ctx.L(id)), E: id => fin(ctx.E(id)), W: (id, w, n = 1) => fin(ctx.W(id, w, n)) });
  const win = (t, a, b, ramp = 0.35) => Math.min(E.inOutSine(P(t, a, a + ramp)), 1 - E.inOutSine(P(t, b, b + ramp)));
  const segs = () => Game.content.script.segments;
  const segIndex = seg => segs().indexOf(seg);
  const fx = (name, alt) => (Audio.has(name) ? name : alt || null);
  const ansOf = (ctx, part) => { const q = part.seg.question; if (!q) return null; return ctx.answer != null ? ctx.answer : part.answer != null ? part.answer : q.correct; };
  function fontOk() { return typeof document === 'undefined' || !document.fonts || document.fonts.status === 'loaded'; }
  const widths = new Map();
  function tw(str, font) {
    const k = font + '|' + str; let w = widths.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) { if (widths.size > 2000) widths.clear(); widths.set(k, w); } }
    return w;
  }
  function alpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  function txt(str, x, y, size, o = {}) {
    g.save(); g.font = `${size}px ${o.font || HANDF}`; g.fillStyle = o.color || INK; g.textAlign = o.align || 'center'; g.textBaseline = 'middle';
    if (o.alpha != null) g.globalAlpha *= o.alpha;
    if (o.rot) { g.translate(x, y); g.rotate(o.rot); g.fillText(str, 0, 0); } else g.fillText(str, x, y);
    g.restore();
  }
  /* the min over a question's reply ids of a cue word per branch (tbl: {replyId: word | [word, n]}) */
  function branch(c, tbl) {
    let best = Infinity;
    for (const id in tbl) { const v = tbl[id]; if (v == null) continue; const [w, n] = Array.isArray(v) ? v : [v, 1]; best = Math.min(best, c.W(id, w, n)); }
    return best;
  }
  /* keyframed placement: keys [[t, x, y, s, rot?]] → {x, y, s, rot}; each move eases over dur from its key time */
  function track(t, keys, dur = 0.6) {
    let x = keys[0][1], y = keys[0][2], s = keys[0][3] ?? 1, r = keys[0][4] ?? 0;
    for (let i = 1; i < keys.length; i++) {
      const k = keys[i]; if (t <= k[0]) continue;
      const e = E.inOutCubic(P(t, k[0], k[0] + dur));
      x = lerp(x, k[1], e); y = lerp(y, k[2], e); s = lerp(s, k[3] ?? s, e); r = lerp(r, k[4] ?? r, e);
    }
    return { x, y, s, rot: r };
  }
  /* typing speed that finishes a quote within its spoken line (≥ 12 chars/s) */
  const cpsFor = (str, c, id) => { const d = c.E(id) - c.L(id); return d > 0 && d < 1e5 ? Math.max(12, str.length / Math.max(0.6, d * 0.85)) : 30; };
  /* the portrait dock: typed quotes and cards as notes (immediate mode) */
  const NOTES = {};
  function note(key, lines) {
    NOTES[key] = lines;
    if (window.UI && UI.docked && UI.docked()) Dock.text('sv-' + key, lines, { cls: 'note' });
  }

  /* ======================================================================
     Layers: static art baked once into an off-screen canvas (the engine's OFF budget evicts them like sprites).
     layer(key, x, y, w, h, draw, {zoom}) — draw() paints the rect (x, y, w, h) in the caller's coordinates (the
     current transform applies to the blit, so a baked print can move and scale).
     ====================================================================== */
  const LY = new Map();
  function layer(key, x, y, w, h, draw, o = {}) {
    const res = +Math.min(3, Math.max(0.3, DS * (o.zoom || 1))).toFixed(3);
    let e = LY.get(key);
    if (!e || e.dead || e.res !== res) {
      if (e && !e.dead) OFF.drop(e);
      LY.delete(key);
      const pw = Math.ceil(w * res), ph = Math.ceil(h * res);
      if (!FX.sprites || typeof _bake !== 'function' || !fontOk() || !OFF.room(pw * ph * 4)) { draw(); return false; }
      const cv = _bake(pw, ph, cx => cx.setTransform(res, 0, 0, res, -x * res, -y * res), draw);
      e = OFF.add(cv, { res, x, y, w, h });
      const me = e; e.drop = () => { if (LY.get(key) === me) LY.delete(key); };
      LY.set(key, e);
    }
    e.frame = OFF.frame;
    g.drawImage(e.c, x, y, e.c.width / res, e.c.height / res);
    return true;
  }

  /* ---------------- paper bits ---------------- */
  function strike(x0, y0, x1, y1, prog, seed = 1, color = RED, w = 8) { ink([[x0, y0], [(x0 + x1) / 2 + 4, (y0 + y1) / 2 - 3], [x1, y1]], { w, color, seed, prog, passes: 2, step: 10 }); }
  /* a sticky note with hand-written lines (x, y = centre) */
  function sticky(lines, x, y, t0, o = {}) {
    const s = pop(T, t0, 0.35); if (s <= 0) return;
    const w = o.w || 300, size = o.size || 34, seed = o.seed || 610, h = Math.round(40 + lines.length * size * 1.12);
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? -0.05); g.scale(s, s);
    paperRect(0, 0, w, h, o.color || '#fff27a', { seed, depth: 1.2 });
    lines.forEach((l, i) => hand(l, 0, -h / 2 + 20 + size * 0.6 + i * size * 1.12, size, t0 + 0.1 + i * 0.15, { color: o.ink || INK, seed: seed + i + 1, dur: 0.3 }));
    tape(0, -h / 2 + 2, 90, 0.05, undefined, seed + 20);
    g.restore();
  }
  /* an index card (white, blue rules, red margin); content() draws in card coordinates (0, 0 = centre) */
  function indexCard(x, y, w, h, s, o, content) {
    if (s <= 0.001) return;
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? -0.02); g.scale(s, s);
    paperRect(0, 0, Math.round(w), Math.round(h), WHITE, { seed: o.seed || 700, depth: 1.3 });
    g.strokeStyle = 'rgba(90,150,190,0.3)'; g.lineWidth = 2; g.beginPath();
    for (let yy = -h / 2 + 64; yy < h / 2 - 8; yy += 50) { g.moveTo(-w / 2 + 12, yy); g.lineTo(w / 2 - 12, yy); }
    g.stroke(); g.strokeStyle = 'rgba(217,53,44,0.4)'; g.beginPath(); g.moveTo(-w / 2 + 56, -h / 2 + 8); g.lineTo(-w / 2 + 56, h / 2 - 8); g.stroke();
    if (o.tape !== false) tape(0, -h / 2 + 2, 120, 0.04, undefined, (o.seed || 700) + 3);
    if (content) content();
    g.restore();
  }
  /* a typed strip: a photocopied paper slip sized to the text; returns {x0 (text left), y, size, w} for marks */
  function slip(str, x, y, size, t0, o = {}) {
    const Zn = Z(), tw_ = Zn.measure(str, size), pad = o.pad ?? size * 1.1;
    const w = Math.round(tw_ + pad * 2), h = Math.round(size * 1.9), s = o.s ?? 1;
    const geo = { x0: x - tw_ / 2, y, size, w, h, str };
    if (s <= 0.001 || T < t0 - 0.05) return geo;
    const k = o.pop === false ? 1 : pop(T, t0 - 0.05, 0.3, 1.6);
    g.save(); g.globalAlpha *= o.alpha ?? 1; g.translate(x, y); g.rotate(o.rot ?? 0); g.scale(s * k, s * k); g.translate(-x, -y);
    Zn.page(x, y, w, h, { seed: o.seed || 50, dark: 0.35, depth: 1.1 });
    if (o.tape) tape(x - w / 2 + 30, y - h / 2 + 6, 80, -0.5, undefined, (o.seed || 50) + 1);
    if (o.num) Zn.type(o.num, x - w / 2 + pad * 0.45, y, size * 0.7, { color: GREY, align: 'center', seed: (o.seed || 50) + 2 });
    Zn.type(str, geo.x0, y, size, { t0, cps: o.cps || 30, seed: o.seed || 50, cursor: true });
    if (o.after) o.after(geo);
    g.restore();
    return geo;
  }
  /* a small photocopied source tag (right-aligned at xr) */
  function srcTag(str, xr, y, t0, seed = 90) {
    if (T < t0) return;
    const Zn = Z(), w = Math.round(Zn.measure(str, 24) + 36);
    Zn.page(xr - w / 2, y, w, 40, { seed, dark: 0.3, depth: 0.8 });
    Zn.type(str, xr - w + 18, y, 24, { color: GREY, seed });
  }
  /* a neon mark under part of a typed string (geo from slip / a page line) */
  function markSub(geo, sub, colour, t0, o = {}) {
    const i = geo.str.indexOf(sub); if (i < 0 || T < t0) return null;
    const Zn = Z(), x = geo.x0 + Zn.measure(geo.str.slice(0, i), geo.size) - 6, w = Zn.measure(sub, geo.size) + 12;
    Zn.highlight(x, geo.y + 2, w, geo.size * 1.12, colour, { t0, dur: o.dur || 0.4, seed: o.seed || 3 });
    return { x, w, y: geo.y };
  }
  /* a rubber stamp (no knock-out specks: they would punch through the whole frame) */
  function stampMark(text, x, y, s, o = {}) {
    if (s <= 0.001) return;
    const size = o.size || 72, color = o.color || RED, font = `${size}px 'Special Elite'`;
    const w = Math.round(tw(text, font) + size * 0.9), h = Math.round(size * 1.5);
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.12); g.scale(s, s); g.globalAlpha *= o.alpha ?? 0.9;
    g.strokeStyle = color; g.fillStyle = color; g.lineJoin = 'round';
    g.lineWidth = size * 0.1; pathPts(rectPts(w, h, 40 + text.length, true, 4)); g.stroke();
    g.lineWidth = size * 0.035; pathPts(rectPts(w - Math.round(size * 0.26), h - Math.round(size * 0.26), 41 + text.length, true, 3)); g.stroke();
    g.font = font; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(text, 0, size * 0.07);
    g.restore();
  }
  /* a device chip's width (Zine.label: the typed name + 2.6 × size for the doodle and padding) */
  const chipW = (id, size, text) => Z().measure((text || (window.Devices ? Devices.name(id) : id)).toUpperCase(), size) + size * 2.6;
  const stampS = (t, t0) => (t < t0 ? 0 : E.outBack(P(t, t0, t0 + 0.25), 1.6));

  /* ======================================================================
     Staging: puppets, captions, chapter cards (after lessons/branches/levels/video.js)
     ====================================================================== */
  const POS = {
    two: { nia: { x: 250, y: 1088, h: 560, dip: 0 }, park: { x: 1665, y: 1088, h: 600, dip: 0 } },
    inset: { nia: { x: 124, y: 1098, h: 300, dip: 56 }, park: { x: 1796, y: 1098, h: 320, dip: 56 } },
  };
  /* keys: [[t, mode] …] → the puppets' {x, y, h} (moves ease over 0.7 s); an inset puppet dips while silent */
  function stage(t, part, keys) {
    const out = {};
    ['nia', 'park'].forEach(who => {
      const v = Object.assign({}, POS[keys[0][1]][who]);
      for (let i = 1; i < keys.length; i++) {
        const k = keys[i]; if (t <= k[0]) continue;
        const e = E.inOutCubic(P(t, k[0], k[0] + 0.7)), p = POS[k[1]][who];
        ['x', 'y', 'h', 'dip'].forEach(f => { v[f] = lerp(v[f], p[f], e); });
      }
      const talk = part.lines.filter(l => l.who === who).reduce((a, l) => Math.max(a, win(t, l.start - 0.35, l.end + 0.15, 0.3)), 0);
      v.y += v.dip * (1 - talk);
      out[who] = v;
    });
    return out;
  }
  const NIA_POINT = new Set(['s1_03', 's1_05', 's2_03', 's3_03', 's4_01', 's4_07', 's5_03', 's5_07']);
  const PARK_POINT = new Set(['s1_04', 's1_07', 's2_01', 's2_04', 's2_05', 's3_02', 's3_04', 's4_03', 's4_04', 's5_01', 's5_04']);
  function cast(t, ctx, st) {
    const cap = ctx.caption, id = cap ? cap.id : '', who = cap ? cap.who : '';
    const np = who === 'nia' && NIA_POINT.has(id) ? 'point' : null;
    const pp = who === 'park' && (PARK_POINT.has(id) || ctx.kind === 'reply') ? 'point' : null;
    Puppet.draw('nia', st.nia.x, st.nia.y, st.nia.h, { env: ctx.speaking('nia'), facing: 1, t, pose: np });
    Puppet.draw('park', st.park.x, st.park.y, st.park.h, { env: ctx.speaking('park'), facing: -1, t, pose: pp });
    bubble(ctx, st);
  }
  /* caption bubbles: ≤ 2 rows (wider first, then paged at sentence ends, timed by the words) */
  const BS = () => (Player.captionSize ? Player.captionSize() : 40);
  const pageCache = new Map();
  function pages(l, w) {
    const size = BS(), key = l.id + '|' + w + '|' + l.tokens.length + '|' + size + '|' + l.dur.toFixed(2);
    if (pageCache.has(key)) return pageCache.get(key);
    const toks = l.tokens, n = toks.length, maxW = w - 56;
    g.save(); g.font = `${size}px ${HANDF}`;
    const sp = g.measureText(' ').width, ws = toks.map(k => g.measureText(k.w).width);
    g.restore();
    const rowsOf = (a, b) => { let r = 1, rw = 0; for (let i = a; i < b; i++) { if (rw > 0 && rw + sp + ws[i] > maxW) { r++; rw = 0; } rw += ws[i] + sp; } return r; };
    const out = []; let a = 0;
    while (a < n) {
      let b = a + 1; while (b < n && rowsOf(a, b + 1) <= 2) b++;
      if (b < n) {
        const lo = a + Math.max(1, Math.floor((b - a) * 0.4));
        let cut_ = -1;
        for (const re of [/[.!?]["')]?$/, /[,;:]$/]) { for (let j = b; j >= lo; j--) if (re.test(toks[j - 1].w)) { cut_ = j; break; } if (cut_ > 0) break; }
        if (cut_ > 0) b = cut_;
      }
      out.push([a, b]); a = b;
    }
    const res = out.map(([a_, b_], k) => {
      const t0 = k === 0 ? 0 : Math.max(0, toks[a_].s - 0.15);
      return { t0, text: toks.slice(a_, b_).map(x => x.w).join(' '), rows: rowsOf(a_, b_), tokens: toks.slice(a_, b_).map(x => ({ w: x.w, s: x.s - t0, e: x.e - t0 })) };
    });
    if (pageCache.size > 300) pageCache.clear();
    if (fontOk()) pageCache.set(key, res);
    return res;
  }
  function fit(l, w0, maxW) {
    const BLH = BS() * 1.18;
    for (let w = w0; w <= maxW; w += 40) { const pg = pages(l, w); if (pg.length === 1) return { w, pages: pg, h: pg[0].rows * BLH + 40 }; }
    const pg = pages(l, w0);
    return { w: w0, pages: pg, h: Math.max(...pg.map(x => x.rows)) * BLH + 40 };
  }
  const pageAt = (pg, lt) => { let k = 0; for (let i = 1; i < pg.length; i++) if (lt >= pg[i].t0) k = i; return pg[k]; };
  /* full size: the top band on the speaker's side (the stage below stays clear); inset: the bottom band beside them */
  function bubble(ctx, st) {
    const l = ctx.caption; if (!l) return;
    const p = st[l.who]; if (!p) return;
    const nia = l.who === 'nia', dir = nia ? 1 : -1, side = nia ? 'left' : 'right', k = BS() / 40;
    const tall = p.h > 420;
    const f = fit(l, Math.round(700 * k), Math.round(860 * k));
    const w = f.w, bh = f.h;
    let cx, cy;
    if (tall) { cx = nia ? p.x - 60 + w / 2 : p.x + 60 - w / 2; cy = 84 + bh / 2; }
    else { cx = p.x + dir * (0.2 * p.h + w / 2 + 16); cy = Math.min(876, 968 - bh / 2); }
    cx = Math.max(w / 2 + 16, Math.min(1904 - w / 2, cx));
    cy = Math.max(bh / 2 + 16, cy);
    const cap = { x: cx, y: cy, side, w };
    if (f.pages.length > 1) { const pg = pageAt(f.pages, ctx.t - l.start); Object.assign(cap, { text: pg.text, tokens: pg.tokens, t0: pg.t0 }); }
    ctx.captionAt = cap;
  }

  /* ---------- chapter cards: a photocopied zine page on the cork, the device of the chapter, tears away ---------- */
  const CARD = { hold: 1.1, tear: 0.7, lead: 2.2 };
  const CH_DEVICE = { s1: 'simile', s2: 'metaphor', s3: 'hyperbole', s4: 'irony', s5: 'rhetoricalQuestion' };
  const cardW = new Map();
  /* a ransom title's size so it fits maxW (widths cached once the fonts are in) */
  function ransomFit(str, seed, maxW, max) {
    const k = str + '|' + seed; let w0 = cardW.get(k);
    if (w0 == null) { w0 = ransom(str, -5000, -5000, 100, 1e9, { seed, palette: COVER_PAL }); if (fontOk()) cardW.set(k, w0); }
    return Math.min(max, (maxW / Math.max(1, w0)) * 100);
  }
  function drawCard(i) {
    const seg = segs()[i]; if (!seg) return;
    const n = i + 1, Zn = Z();
    Zn.cork();
    Zn.page(960, 530, 1320, 820, { seed: 900 + n, rot: -0.012 + (n % 2) * 0.02, staple: 'left', tape: 1, dark: 0.55 });
    Zn.type('PAGE ' + n + ' OF 5', 960, 250, 44, { align: 'center', seed: 910 + n });
    const title = String(seg.chapter || seg.id).toUpperCase();
    const size = ransomFit(title, 950 + n, 1100, 124);
    ransom(title, 960, 480, size, -1e6, { seed: 950 + n, palette: COVER_PAL });
    const dev = CH_DEVICE[seg.id];
    if (dev) Zn.label(dev, 960, 660, { size: 38, rot: -0.03, seed: 960 + n });
    Zn.type('~ ' + String((Game.lesson && Game.lesson.title) || '').toLowerCase() + ' ~', 960, 790, 32, { align: 'center', color: GREY, seed: 970 + n });
  }
  function chapterSfx(part) { return part.kind === 'lines' ? [{ name: 'tear', t: CARD.hold, gain: 0.5 }] : []; }

  const BODY = {};
  const MARKS = {};    // QA: which reply marks are on screen (per segment)

  /* ======================================================================
     s1 "Extra steps": the library corkboard, the slam flyer, Nia's note card, LITERAL vs FIGURATIVE, the three strips
     ====================================================================== */
  const S1 = { fly: { x: 720, y: 560, w: 470, h: 640 }, card: { x: 1275, y: 450, w: 580, h: 330 }, lit: { x: 960, y: 430, w: 1240, h: 380 }, stripX: 420, stripY: [330, 480, 630] };
  function flyer(t, tm, pl) {
    const { w, h } = S1.fly, Zn = Z();
    g.save(); g.translate(pl.x, pl.y); g.rotate(pl.rot); g.scale(pl.s, pl.s);
    Zn.page(0, 0, w, h, { seed: 21, staple: 'top', tape: 1, dark: 0.6 });
    ransom('OPEN MIC', 0, -h / 2 + 90, ransomFit('OPEN MIC', 23, w - 70, 70), -1e6, { seed: 23, palette: COVER_PAL });
    Zn.type('POETRY SLAM · FRIDAY', 0, -h / 2 + 170, 30, { align: 'center', seed: 24 });
    Zn.art('microphone_stand', -110, 150, 250, { seed: 25 });
    Zn.type('SIGN UP:', 10, -30, 28, { seed: 26 });
    for (let k = 0; k < 4; k++) ink([[10, 30 + k * 50], [w / 2 - 30, 30 + k * 50]], { w: 2, color: 'rgba(20,18,16,0.5)', seed: 27 + k, passes: 1, step: 60 });
    hand('Nia B.', 90, 14, 44, tm.sign, { color: RED, seed: 31, dur: 0.5, rot: -0.04 });
    hand('(to heckle)', 105, 62, 30, tm.heckle, { color: RED, seed: 32, dur: 0.4, rot: -0.03 });
    g.restore();
  }
  function noteCard(t, tm, pl) {
    const { w, h } = S1.card;
    indexCard(pl.x, pl.y, w, h, pl.s * pop(t, tm.card, 0.35, 1.8), { rot: pl.rot + 0.03, seed: 41 }, () => {
      hand("Nia's argument:", -w / 2 + 80, -h / 2 + 38, 28, tm.card + 0.1, { align: 'left', color: GREY, seed: 42, dur: 0.3 });
      hand('FIGURATIVE LANGUAGE', 0, -h / 2 + 110, 50, tm.card + 0.2, { color: INK, seed: 43, dur: 0.5 });
      hand('= LYING WITH', 0, -h / 2 + 175, 50, tm.lying, { color: RED, seed: 44, dur: 0.4 });
      hand('EXTRA STEPS', 0, -h / 2 + 240, 58, tm.extra, { color: RED, seed: 45, dur: 0.4 });
      if (T > tm.extra + 0.4) ink([[-200, -h / 2 + 280], [200, -h / 2 + 276]], { w: 5, color: RED, seed: 46, prog: P(T, tm.extra + 0.4, tm.extra + 0.7), passes: 1 });
    });
  }
  function litPage(t, tm, a) {
    const Zn = Z(), L_ = LINES(), P_ = S1.lit, x0 = P_.x - P_.w / 2 + 90;
    alpha(a, () => {
      const s = pop(t, tm.lit, 0.35, 1.6);
      if (s <= 0) return;
      g.save(); g.translate(P_.x, P_.y); g.scale(s, s); g.translate(-P_.x, -P_.y);
      Zn.page(P_.x, P_.y, P_.w, P_.h, { seed: 61, tape: 1, dark: 0.45 });
      Zn.type('LITERAL:', x0, P_.y - 110, 30, { color: GREY, seed: 62 });
      Zn.type(L_.literal || 'The hallway was loud.', x0, P_.y - 60, 46, { t0: tm.lit, cps: 26, seed: 63, cursor: true });
      if (T > tm.fig - 0.1) Zn.type('FIGURATIVE:', x0, P_.y + 40, 30, { color: GREY, seed: 64 });
      Zn.type(L_.q1_1 || 'The hallway was as loud as a stadium.', x0, P_.y + 90, 46, { t0: tm.fig, cps: 26, seed: 65, cursor: true });
      g.restore();
    });
  }
  BODY.s1 = function (t, part, ctx, c) {
    const Zn = Z(), L_ = LINES(), q = part.seg.question;
    Zn.cork();
    const tm = {
      sign: c.W('s1_01', 'signing'), heckle: c.W('s1_01', 'heckle'),
      card: c.W('s1_03', 'Figurative'), lying: c.W('s1_03', 'lying'), extra: c.W('s1_03', 'extra'),
      lit: c.W('s1_04', 'literal', 2), fig: c.W('s1_04', 'figurative', 2), move: c.L('s1_04'),
      strips: part.kind === 'lines' ? c.E('s1_05') + 0.4 : -1e6,
      magic: c.W('s1_06', 'magic'), simile: c.W('s1_07', 'simile'),
    };
    // the flyer and Nia's note card move to the corners when Ms. Park's lines start (s1_04)
    const fl = track(t, [[0, S1.fly.x, S1.fly.y, 1, -0.03], [tm.move, 250, 320, 0.42, -0.06]], 0.8);
    const nc = track(t, [[0, S1.card.x, S1.card.y, 1, 0], [tm.move, 1660, 300, 0.5, 0.05]], 0.8);
    flyer(t, tm, fl);
    noteCard(t, tm, nc);
    note('flyer', ['OPEN MIC · POETRY SLAM · FRIDAY']);
    if (t > tm.card) note('nia', ["Nia's note: FIGURATIVE LANGUAGE = LYING WITH EXTRA STEPS"]);
    // LITERAL vs FIGURATIVE (s1_04) until the question's strips arrive
    const litA = 1 - P(t, tm.strips, tm.strips + 0.35);
    if (t > tm.lit - 0.1 && litA > 0) {
      litPage(t, tm, litA);
      note('lit', [`LITERAL: ${L_.literal}`].concat(t > tm.fig ? [`FIGURATIVE: ${L_.q1_1}`] : []));
    }
    // the three options as typed strips (the question), marked in the reply
    const geos = [];
    if (t > tm.strips) {
      q.options.forEach((o, i) => {
        const gw = Zn.measure(o, 44), x = S1.stripX + gw / 2 + 60, y = S1.stripY[i];
        geos[i] = slip(o, x, y, 44, tm.strips + 0.2 + i * 0.25, { seed: 70 + i, num: String(i + 1), pad: 70, cps: 60, tape: i === 1 });
      });
      const tHi = branch(c, { q1_right: 'hallway', q1_wrong0: 'hallway', q1_wrong2: 'hallway' });
      const tVerb = branch(c, { q1_right: 'different', q1_wrong0: 'verb', q1_wrong2: 'comparison' });
      const tRain = branch(c, { q1_right: 'using', q1_wrong0: 'Nothing', q1_wrong2: 'means' });
      // 1: "as loud as" in the simile's yellow + the two-wavy-line doodle
      const g1 = geos[1], hi = markSub(g1, 'as loud as', COL('simile'), tHi, { seed: 7 });
      if (hi) Zn.doodle('simile', g1.x0 + Zn.measure(g1.str, 44) + 90, g1.y, 34, { t0: tHi + 0.3, dur: 0.5, seed: 8 });
      // 0: "like" in "I like pizza" circled: a verb
      const g0 = geos[0], li = g0.str.indexOf('like');
      if (li >= 0 && t > tVerb) {
        const lx = g0.x0 + Zn.measure(g0.str.slice(0, li), 44) + Zn.measure('like', 44) / 2;
        inkCircle(lx, g0.y, 58, { sx: 1.05, sy: 0.6, w: 6, color: RED, seed: 81, prog: P(t, tVerb, tVerb + 0.4) });
        const nx = g0.x0 + Zn.measure(g0.str, 44) + 170;
        sticky(['a verb'], nx, g0.y - 4, tVerb + 0.3, { w: 170, size: 40, seed: 83, rot: -0.05, color: WHITE, ink: RED });
      }
      // 2: "It looks like rain" gets a note: a phrase
      if (t > tRain) sticky(['a phrase meaning:', 'I think it will rain'], geos[2].x0 + Zn.measure(geos[2].str, 44) + 290, S1.stripY[2] + 10, tRain, { w: 390, size: 32, seed: 84, rot: 0.04 });
      MARKS.s1 = { simile: t > tHi + 0.4, verb: t > tVerb + 0.4, rain: t > tRain + 0.4 };
      if (t > tHi) note('q1', ['Simile: "as loud as" compares the hallway to a stadium.'].concat(t > tVerb ? ['"I like pizza": here "like" is a verb.'] : [], t > tRain ? ['"It looks like rain": a phrase meaning "I think it will rain."'] : []));
    }
    // after: "like isn't a magic simile button" / "A simile compares two different things"
    if (t > tm.magic) {
      const s = pop(t, tm.magic, 0.35, 2);
      g.save(); g.translate(640, 170); g.scale(s, s);
      paperCircle(0, 0, 62, RED, { seed: 91, depth: 1.2 });
      paperCircle(0, 0, 48, '#e8554a', { seed: 92, depth: 0.2 });
      txt('LIKE', 0, 2, 34, { font: Zn.FONT, color: WHITE });
      g.restore();
      if (t > tm.magic + 0.35) { strike(580, 110, 700, 230, P(t, tm.magic + 0.35, tm.magic + 0.6), 93, INK, 9); strike(700, 110, 580, 230, P(t, tm.magic + 0.5, tm.magic + 0.75), 94, INK, 9); }
      hand('not a magic simile button', 740, 172, 38, tm.magic + 0.5, { align: 'left', color: INK, seed: 95, dur: 0.4, rot: -0.02 });
      note('magic', ['"like" is not a magic simile button: it is the signal, not the proof.']);
    }
    if (t > tm.simile) {
      Zn.label('simile', 1440, 150, { size: 36, rot: 0.03, s: pop(t, tm.simile, 0.35, 1.8), seed: 96 });
      Zn.type('compares two different things', 1440, 222, 30, { t0: tm.simile + 0.3, cps: 40, align: 'center', seed: 97 });
      note('simile', ['SIMILE: compares two different things, usually with like or as.']);
    }
    const st = stage(t, part, part.kind === 'lines' ? [[0, 'two'], [tm.move - 0.3, 'inset']] : [[0, 'inset']]);
    cast(t, ctx, st);
  };
  S.s1 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 1.4, after: 1.8 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: 'scribble', t: c.W('s1_01', 'signing'), gain: 0.25 }, { name: 'pop', t: c.W('s1_03', 'Figurative'), gain: 0.3 });
      out.push({ name: fx('typewriter'), t: c.W('s1_04', 'literal', 2), gain: 0.35 }, { name: fx('typewriter'), t: c.W('s1_04', 'figurative', 2), gain: 0.35 });
      if (part.kind === 'lines') [0, 1, 2].forEach(i => out.push({ name: 'paper', t: c.E('s1_05') + 0.6 + i * 0.25, gain: 0.25 }));
      out.push({ name: 'scribble', t: branch(c, { q1_right: 'hallway', q1_wrong0: 'hallway', q1_wrong2: 'hallway' }), gain: 0.3 });
      out.push({ name: 'scribble', t: branch(c, { q1_right: 'different', q1_wrong0: 'verb', q1_wrong2: 'comparison' }), gain: 0.25 });
      out.push({ name: 'pop', t: branch(c, { q1_right: 'using', q1_wrong0: 'Nothing', q1_wrong2: 'means' }), gain: 0.3 });
      out.push({ name: 'boing', t: c.W('s1_06', 'magic'), gain: 0.3 }, { name: 'pop', t: c.W('s1_07', 'simile'), gain: 0.3 });
      return out;
    },
  };

  /* ======================================================================
     s2 "Juliet is the sun": the linocut balcony, the Juliet line typed + pink, Sonnet 130 with the sticky
     ====================================================================== */
  const S2 = { print: { x: 960, y: 430, w: 760, h: 420 }, stripY: 770, son: { x: 960, y: 470, w: 1180, h: 270 } };
  /* the linocut print (local coords, centre 0, 0; 740×400): night, a crescent moon, the sun, a balcony under an arch */
  function balconyPrint() {
    const W = 740, H = 400, r = rng(211);
    g.fillStyle = INK; g.fillRect(-W / 2, -H / 2, W, H);
    g.fillStyle = WHITE;
    for (let i = 0; i < 40; i++) { const x = -W / 2 + r() * W, y = -H / 2 + r() * H * 0.45; g.beginPath(); g.arc(x, y, 1 + r() * 2.2, 0, TAU); g.fill(); }
    // the moon (a crescent) at the left
    g.beginPath(); g.arc(-255, -110, 46, 0, TAU); g.fill();
    g.fillStyle = INK; g.beginPath(); g.arc(-236, -122, 42, 0, TAU); g.fill();
    // the sun at the right: a disc, carved rings, rays
    g.fillStyle = WHITE; g.beginPath(); g.arc(250, -105, 52, 0, TAU); g.fill();
    g.strokeStyle = INK; g.lineWidth = 5; g.beginPath(); g.arc(250, -105, 34, 0, TAU); g.stroke();
    g.beginPath(); g.arc(250, -105, 16, 0, TAU); g.stroke();
    for (let k = 0; k < 14; k++) {
      const a = k / 14 * TAU, a1 = a - 0.09, a2 = a + 0.09;
      g.beginPath(); g.moveTo(250 + Math.cos(a1) * 62, -105 + Math.sin(a1) * 62); g.lineTo(250 + Math.cos(a) * (92 + (k % 2) * 14), -105 + Math.sin(a) * (92 + (k % 2) * 14)); g.lineTo(250 + Math.cos(a2) * 62, -105 + Math.sin(a2) * 62); g.closePath(); g.fill();
    }
    // the wall: carved brick lines on the lower half
    g.strokeStyle = 'rgba(255,254,250,0.8)'; g.lineWidth = 3;
    for (let row = 0; row < 5; row++) {
      const y = 30 + row * 34;
      g.beginPath(); g.moveTo(-W / 2 + 6, y); g.lineTo(-130, y + (r() - 0.5) * 3); g.moveTo(130, y); g.lineTo(W / 2 - 6, y + (r() - 0.5) * 3); g.stroke();
      for (let k = 0; k < 6; k++) { const x = -W / 2 + 20 + k * 40 + (row % 2) * 20; g.beginPath(); g.moveTo(x, y); g.lineTo(x, y + 34); g.stroke(); const x2 = 150 + k * 38 + (row % 2) * 19; g.beginPath(); g.moveTo(x2, y); g.lineTo(x2, y + 34); g.stroke(); }
    }
    // the arched window (lit) above the balcony
    g.fillStyle = WHITE; g.beginPath(); g.moveTo(-80, 70); g.lineTo(-80, -40); g.arc(0, -40, 80, Math.PI, TAU); g.lineTo(80, 70); g.closePath(); g.fill();
    g.fillStyle = INK; g.beginPath(); g.moveTo(-62, 70); g.lineTo(-62, -40); g.arc(0, -40, 62, Math.PI, TAU); g.lineTo(62, 70); g.closePath(); g.fill();
    g.strokeStyle = WHITE; g.lineWidth = 4; g.beginPath(); g.moveTo(0, -100); g.lineTo(0, 70); g.moveTo(-62, 0); g.lineTo(62, 0); g.stroke();
    // the balcony: ledge, balusters, base
    g.fillStyle = WHITE;
    g.fillRect(-160, 64, 320, 22); g.fillRect(-170, 168, 340, 20);
    for (let k = 0; k < 9; k++) { const x = -140 + k * 35; g.beginPath(); g.ellipse(x, 110, 9, 16, 0, 0, TAU); g.fill(); g.fillRect(x - 5, 86, 10, 82); g.beginPath(); g.ellipse(x, 146, 8, 12, 0, 0, TAU); g.fill(); }
    g.fillStyle = INK; for (let k = 0; k < 9; k++) g.fillRect(-140 + k * 35 - 1, 94, 2, 66);
    // gouge marks round the edge (linocut texture)
    g.strokeStyle = 'rgba(255,254,250,0.55)'; g.lineWidth = 2.5;
    for (let i = 0; i < 60; i++) {
      const edge = r() < 0.5, x = edge ? (r() < 0.5 ? -W / 2 + 8 + r() * 40 : W / 2 - 8 - r() * 40) : -W / 2 + r() * W, y = edge ? -H / 2 + r() * H : H / 2 - 6 - r() * 26;
      const a = r() * Math.PI, L = 6 + r() * 14; g.beginPath(); g.moveTo(x, y); g.lineTo(x + Math.cos(a) * L, y + Math.sin(a) * L); g.stroke();
    }
  }
  function print(t, pl, glow) {
    const { w, h } = S2.print, Zn = Z();
    if (pl.s <= 0.001) return;
    g.save(); g.translate(pl.x, pl.y); g.rotate(pl.rot); g.scale(pl.s, pl.s);
    Zn.page(0, 0, w + 60, h + 70, { seed: 201, tape: 2, dark: 0.35 });
    layer('s2_print', -370, -200, 740, 400, balconyPrint);
    if (glow > 0) {
      g.save(); g.globalCompositeOperation = 'lighter'; g.globalAlpha *= glow * 0.5;
      const gr = g.createRadialGradient(250, -105, 40, 250, -105, 200); gr.addColorStop(0, 'rgba(255,240,170,0.9)'); gr.addColorStop(1, 'rgba(255,240,170,0)');
      g.fillStyle = gr; g.beginPath(); g.rect(-370, -200, 740, 400); g.fill(); g.restore();
    }
    Zn.type('linocut · the balcony', w / 2 - 10, h / 2 + 18, 22, { align: 'right', color: GREY, seed: 202 });
    g.restore();
  }
  BODY.s2 = function (t, part, ctx, c) {
    const Zn = Z(), L_ = LINES();
    Zn.cork();
    const jul = L_.juliet || 'It is the east, and Juliet is the sun!', son = L_.sonnet130 || "My mistress' eyes are nothing like the sun";
    const tm = {
      print: CARD.hold + 0.3, jul: c.L('s2_02'), cps: cpsFor(jul, c, 's2_02'),
      hi: branch(c, { q2_right: 'Juliet', q2_wrong0: 'Romeo', q2_wrong2: 'Juliet' }),
      eq: branch(c, { q2_right: 'no', q2_wrong0: 'metaphor', q2_wrong2: 'metaphor' }),
      lights: c.W('s2_04', 'lights'), son: c.W('s2_05', 'mistress'), like2: c.W('s2_05', 'like', 2), joke: c.W('s2_05', 'joking'),
    };
    if (part.kind !== 'lines') tm.print = -1e6;
    const pl = track(t, [[0, S2.print.x, S2.print.y, 1, -0.01], [tm.son - 0.6, 520, 250, 0.5, -0.05]], 0.7);
    print(t, { ...pl, s: pl.s * pop(t, tm.print, 0.4, 1.6) }, P(t, tm.lights, tm.lights + 0.4));
    // the Juliet line types itself (s2_02), pink in the reply
    const jA = 1 - 0.55 * P(t, tm.son - 0.4, tm.son);
    let jg = null;
    if (t > tm.jul - 0.1) alpha(jA, () => {
      jg = slip(jul, 960, S2.stripY, 50, tm.jul, { seed: 221, cps: tm.cps, tape: true });
      srcTag('Shakespeare, Romeo and Juliet (II.ii)', 960 + jg.w / 2 - 20, S2.stripY + 72, tm.jul + 0.4, 222);
      const hi = markSub(jg, 'Juliet is the sun', COL('metaphor'), tm.hi, { seed: 223 });
      if (hi) Zn.label('metaphor', 560, S2.stripY - 110, { size: 32, rot: -0.05, s: pop(t, tm.hi + 0.3, 0.35, 1.8), seed: 224 });
    });
    if (t > tm.jul) note('juliet', [`"${jul}" (Shakespeare, Romeo and Juliet)`]);
    // "JULIET = SUN: no like, no as" (the reply): a sticky with an arrow to the highlighted words
    const eqA = 1 - P(t, tm.son - 0.4, tm.son);
    if (t > tm.eq && eqA > 0) alpha(eqA, () => {
      sticky(['JULIET = SUN', 'no like, no as'], 1385, 585, tm.eq, { w: 330, size: 38, seed: 231, rot: 0.05, color: '#ffb3dc' });
      if (jg) inkArrow(1330, 668, jg.x0 + Zn.measure(jg.str, 50) - 150, S2.stripY - 40, { w: 5, color: INK, seed: 232, prog: P(t, tm.eq + 0.3, tm.eq + 0.6), head: 18, bulge: 20 });
      note('metaphor', ['METAPHOR: JULIET = SUN. No like, no as.']);
    });
    MARKS.s2 = { pink: t > tm.hi + 0.4, eq: t > tm.eq + 0.4 };
    // Sonnet 130 (s2_05): has LIKE, but says she is NOT like the sun
    if (t > tm.son - 0.1) {
      const S_ = S2.son, s = pop(t, tm.son - 0.1, 0.4, 1.6);
      g.save(); g.translate(S_.x, S_.y); g.rotate(0.012); g.scale(s, s); g.translate(-S_.x, -S_.y);
      Zn.page(S_.x, S_.y, S_.w, S_.h, { seed: 241, staple: 'top', dark: 0.45 });
      Zn.type('SONNET 130', S_.x - S_.w / 2 + 70, S_.y - 80, 30, { color: GREY, seed: 242 });
      const sx = S_.x - Zn.measure(son, 46) / 2, sg = { x0: sx, y: S_.y, size: 46, str: son };
      Zn.type(son, sx, S_.y, 46, { t0: tm.son, cps: cpsFor(son, c, 's2_05') * 1.6, seed: 243 });
      Zn.type('Shakespeare, 1609', S_.x + S_.w / 2 - 60, S_.y + 86, 24, { align: 'right', color: GREY, seed: 244 });
      const li = son.indexOf('like');
      if (li >= 0 && t > tm.like2) inkCircle(sx + Zn.measure(son.slice(0, li), 46) + Zn.measure('like', 46) / 2, S_.y, 54, { sx: 1.3, sy: 0.7, w: 6, color: RED, seed: 245, prog: P(t, tm.like2, tm.like2 + 0.4) });
      g.restore();
      note('sonnet', [`"${son}" (Shakespeare, Sonnet 130)`]);
    }
    if (t > tm.joke) {
      sticky(["has LIKE, but it's saying", 'she is NOT like the sun'], 1250, 950, tm.joke, { w: 450, size: 34, seed: 251, rot: -0.03 });   // below the Juliet strip + its credit (never over "Juliet is the sun")
      note('sonnet-note', ["Has LIKE, but it's saying she is NOT like the sun."]);
    }
    const st = stage(t, part, [[0, 'two']]);
    cast(t, ctx, st);
  };
  S.s2 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 1.4, after: 1.8 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: 'paper', t: CARD.hold + 0.3, gain: 0.3 }, { name: fx('typewriter'), t: c.L('s2_02'), gain: 0.35 });
      out.push({ name: 'scribble', t: branch(c, { q2_right: 'Juliet', q2_wrong0: 'Romeo', q2_wrong2: 'Juliet' }), gain: 0.3 });
      out.push({ name: 'pop', t: branch(c, { q2_right: 'no', q2_wrong0: 'metaphor', q2_wrong2: 'metaphor' }), gain: 0.3 });
      out.push({ name: 'sparkle', t: c.W('s2_04', 'lights'), gain: 0.25 }, { name: 'whoosh', t: c.W('s2_05', 'mistress') - 0.1, gain: 0.25 });
      out.push({ name: fx('typewriter'), t: c.W('s2_05', 'mistress'), gain: 0.3 }, { name: 'scribble', t: c.W('s2_05', 'like', 2), gain: 0.25 }, { name: 'pop', t: c.W('s2_05', 'joking'), gain: 0.3 });
      return out;
    },
  };

  /* ======================================================================
     s3 "A ton of backpack": the backpack thud, orange highlighter, the crossed-out ton scale, HYPERBOLE vs LYING
     ====================================================================== */
  const S3 = { bag: { x: 760, base: 810, h: 400 }, stripY: 300, scale: { x: 1250, y: 610, w: 380, h: 320 }, card: { x: 960, y: 470, w: 1200, h: 330 } };
  function bgS3() {
    layer('bg_s3', 0, 0, 1920, 1080, () => {
      Z().cork();
      paperRect(960, 960, 2060, 320, '#9a6a3e', { seed: 301, torn: true, depth: 1.4, boil: false });
      paperRect(960, 806, 2060, 26, '#b98352', { seed: 302, depth: 0.6, boil: false });
      for (let i = 0; i < 8; i++) ink([[0, 850 + i * 26 + (i % 2) * 4], [1920, 852 + i * 26]], { w: 1.6, color: 'rgba(60,35,15,0.3)', seed: 310 + i, passes: 1, step: 120 });
      // a stack of library books at the table's far right
      [['#2b4a8c', 180], ['#d9352c', 150], ['#3c8a5a', 200]].forEach(([col, w], k) => paperRect(1520 - k * 6, 780 - k * 34, w, 32, col, { seed: 320 + k, depth: 0.8, boil: false }));
    }, { zoom: 0.75 });
  }
  /* the linocut backpack (local coords: base centre at 0, 0; 330 wide, 400 tall) */
  function backpackArt() {
    const body = () => { g.beginPath(); g.moveTo(-150, 0); g.lineTo(-155, -250); g.bezierCurveTo(-155, -400, 155, -400, 155, -250); g.lineTo(150, 0); g.closePath(); };
    g.lineJoin = 'round';
    body(); g.lineWidth = 26; g.strokeStyle = WHITE; g.stroke(); g.fillStyle = INK; g.fill();
    // the handle loop
    g.lineWidth = 16; g.strokeStyle = INK; g.beginPath(); g.moveTo(-40, -350); g.bezierCurveTo(-40, -420, 40, -420, 40, -350); g.stroke();
    g.lineWidth = 3; g.strokeStyle = WHITE; g.beginPath(); g.moveTo(-40, -352); g.bezierCurveTo(-40, -410, 40, -410, 40, -352); g.stroke();
    // carved: the top seam, the front pocket, the zipper, the straps' edges
    g.strokeStyle = WHITE; g.lineWidth = 5;
    g.beginPath(); g.moveTo(-130, -250); g.bezierCurveTo(-130, -370, 130, -370, 130, -250); g.stroke();
    g.beginPath(); g.moveTo(-110, -30); g.lineTo(-115, -170); g.quadraticCurveTo(-115, -200, -85, -200); g.lineTo(85, -200); g.quadraticCurveTo(115, -200, 115, -170); g.lineTo(110, -30); g.closePath(); g.stroke();
    g.setLineDash([8, 7]); g.beginPath(); g.moveTo(-100, -178); g.lineTo(100, -178); g.stroke(); g.setLineDash([]);
    g.fillStyle = WHITE; g.fillRect(88, -188, 10, 24);
    g.beginPath(); g.moveTo(-150, -230); g.lineTo(-128, -20); g.moveTo(150, -230); g.lineTo(128, -20); g.stroke();
    const r = rng(331); g.lineWidth = 2.5; g.strokeStyle = 'rgba(255,254,250,0.6)';
    for (let i = 0; i < 40; i++) { const x = -100 + r() * 200, y = -150 + r() * 120, a = r() * Math.PI, L = 5 + r() * 10; g.beginPath(); g.moveTo(x, y); g.lineTo(x + Math.cos(a) * L, y + Math.sin(a) * L); g.stroke(); }
  }
  function backpack(t, tm) {
    const B = S3.bag, land = tm.land, t0 = land - 0.4;
    if (t < t0) return;
    const u = P(t, t0, land), y = lerp(-500, B.base, u * u);
    const sq = Math.sin(Math.PI * P(t, land, land + 0.22)) * 0.1;
    g.save(); g.translate(B.x, y); g.scale((1 + sq) * B.h / 400, (1 - sq) * B.h / 400); g.rotate((1 - u) * 0.25);
    layer('s3_bag', -175, -440, 350, 455, backpackArt);
    g.restore();
  }
  function scaleCard(t, tm) {
    const C = S3.scale, Zn = Z();
    indexCard(C.x, C.y, C.w, C.h, pop(t, tm.scale, 0.35, 1.8), { rot: 0.03, seed: 341 }, () => {
      // the bathroom scale doodle
      ink([[-120, 90], [120, 90], [110, 30], [-110, 30], [-120, 90]], { w: 5, color: INK, seed: 342, passes: 1, step: 10 });
      inkCircle(0, -30, 58, { w: 5, color: INK, seed: 343 });
      for (let k = 0; k < 7; k++) { const a = Math.PI * (1.1 + k * 0.8 / 6); ink([[Math.cos(a) * 46, -30 + Math.sin(a) * 46], [Math.cos(a) * 56, -30 + Math.sin(a) * 56]], { w: 3, color: INK, seed: 344 + k, passes: 1 }); }
      const na = Math.PI * (1.1 + 0.8 * E.outElastic(P(T, tm.scale + 0.2, tm.scale + 1.2)));
      ink([[0, -30], [Math.cos(na) * 48, -30 + Math.sin(na) * 48]], { w: 5, color: RED, seed: 351, passes: 1 });
      Zn.type(`1 TON = ${TON_LB.toLocaleString('en-US')} lb`, 0, -122, 36, { align: 'center', t0: tm.scale + 0.15, cps: 40, seed: 352 });
      if (T > tm.cross) strike(-150, -108, 150, -136, P(T, tm.cross, tm.cross + 0.35), 353, RED, 6);
    });
    if (t > tm.cross + 0.3) {
      sticky(['nobody believes this:', "that's the point"], C.x + 10, C.y + C.h / 2 + 70, tm.cross + 0.3, { w: 360, size: 34, seed: 354, rot: -0.03, color: WHITE, ink: RED });
    }
  }
  BODY.s3 = function (t, part, ctx, c) {
    const Zn = Z(), L_ = LINES();
    const bp = L_.backpack || 'This backpack weighs a ton.';
    const tm = {
      land: c.W('s3_01', 'Ugh') + 0.2, type: c.W('s3_01', 'This'), scale: c.W('s3_02', 'Two'),
      hi: branch(c, { q3_right: 'hyperbole', q3_wrong1: 'hyperbole', q3_wrong2: 'hyperbole' }),
      cross: branch(c, { q3_right: 'believe', q3_wrong1: 'Nobody', q3_wrong2: 'exaggeration' }),
      card: c.L('s3_04') - 0.2, lie: c.W('s3_04', 'lie'), hyp: c.W('s3_04', 'Hyperbole'),
    };
    if (part.kind !== 'lines') tm.land = -1e6;
    // the thud: a short jolt of the whole table
    const jolt = Math.sin(P(t, tm.land, tm.land + 0.3) * Math.PI * 3) * 9 * (1 - P(t, tm.land, tm.land + 0.3));
    g.save(); g.translate(0, jolt);
    bgS3();
    backpack(t, tm);
    // the typed line (s3_01), orange in the reply
    let sg = null;
    if (t > tm.type - 0.1) {
      sg = slip(bp, 960, S3.stripY, 52, tm.type, { seed: 361, cps: cpsFor(bp, c, 's3_01') * 1.4, tape: true });
      const hi = markSub(sg, 'weighs a ton', COL('hyperbole'), tm.hi, { seed: 362 });
      if (hi) Zn.label('hyperbole', 960 + sg.w / 2 + 40, S3.stripY - 60, { size: 30, rot: 0.08, s: pop(t, tm.hi + 0.3, 0.35, 1.8), seed: 363 });
      note('backpack', [`"${bp}"`].concat(t > tm.hi ? ['HYPERBOLE: a huge exaggeration nobody is meant to believe.'] : []));
    }
    if (t > tm.scale) {
      scaleCard(t, tm);
      note('scale', [`1 TON = ${TON_LB.toLocaleString('en-US')} lb`].concat(t > tm.cross ? ["Crossed out: nobody believes this. That's the point."] : []));
    }
    MARKS.s3 = { orange: t > tm.hi + 0.4, cross: t > tm.cross + 0.4 };
    // Ms. Park's card (s3_04): HYPERBOLE vs LYING
    if (t > tm.card) {
      const K = S3.card, Zi = Zn;
      indexCard(K.x, K.y, K.w, K.h, pop(t, tm.card, 0.4, 1.6), { rot: -0.012, seed: 371 }, () => {
        Zi.type("Ms. Park's card", -K.w / 2 + 80, -K.h / 2 + 36, 26, { color: GREY, seed: 372 });
        const lx = -K.w / 2 + 90;
        const w1 = chipW('hyperbole', 32), w2 = chipW('none', 32, 'LYING'), tx = lx + Math.max(w1, w2) + 24;
        const d1 = 'exaggeration not meant to be taken literally.', d2 = 'trying to make someone believe something false.';
        if (T > tm.hyp) Zi.highlight(tx - 6, -40, Zi.measure(d1, 32) + 12, 44, COL('hyperbole'), { t0: tm.hyp, dur: 0.5, seed: 377 });
        Zi.label('hyperbole', lx + w1 / 2, -40, { size: 32, rot: -0.02, seed: 373 });
        Zi.type(d1, tx, -40, 32, { t0: tm.card + 0.3, cps: 60, seed: 374 });
        Zi.label('none', lx + w2 / 2, 70, { size: 32, rot: 0.02, seed: 375, text: 'LYING' });
        Zi.type(d2, tx, 70, 32, { t0: tm.card + 0.6, cps: 60, seed: 376 });
        if (T > tm.lie) ink([[tx, 96], [tx + Zi.measure(d2, 32), 94]], { w: 4, color: RED, seed: 378, prog: P(T, tm.lie, tm.lie + 0.5), passes: 1 });
      });
      note('card', ['HYPERBOLE: exaggeration not meant to be taken literally.', 'LYING: trying to make someone believe something false.']);
    }
    g.restore();
    const st = stage(t, part, part.kind === 'after' ? [[0, 'two'], [tm.card - 0.3, 'inset']] : [[0, 'two']]);
    cast(t, ctx, st);
  };
  S.s3 = {
    timing: { lead: { lines: CARD.lead + 0.4, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 1.4, after: 2.0 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      if (part.kind === 'lines') out.push({ name: 'whoosh', t: c.W('s3_01', 'Ugh') - 0.2, gain: 0.25 }, { name: 'thunk', t: c.W('s3_01', 'Ugh') + 0.2, gain: 0.8 }, { name: 'bonk', t: c.W('s3_01', 'Ugh') + 0.22, gain: 0.3 });
      out.push({ name: fx('typewriter'), t: c.W('s3_01', 'This'), gain: 0.3 }, { name: 'pop', t: c.W('s3_02', 'Two'), gain: 0.3 }, { name: 'boing', t: c.W('s3_02', 'Two') + 0.3, gain: 0.2 });
      out.push({ name: 'scribble', t: branch(c, { q3_right: 'hyperbole', q3_wrong1: 'hyperbole', q3_wrong2: 'hyperbole' }), gain: 0.3 });
      out.push({ name: 'scribble', t: branch(c, { q3_right: 'believe', q3_wrong1: 'Nobody', q3_wrong2: 'exaggeration' }), gain: 0.3 });
      out.push({ name: 'paper', t: c.L('s3_04') - 0.2, gain: 0.35 });
      return out;
    },
  };

  /* ======================================================================
     s4 "Break a leg": rain on the window, the BREAK A LEG sticky, IDIOM and ALLUSION cards, the picnic balloon
     ====================================================================== */
  const S4 = { win: { x: 1200, y: 420, w: 640, h: 500 }, locker: { x: 580, y: 560, w: 320, h: 860 }, idiom: { x: 1010, y: 300, w: 900, h: 240 }, allu: { x: 1030, y: 575, w: 980, h: 250 }, balloon: { x: 1130, y: 350 } };
  function bgS4() {
    layer('bg_s4', 0, 0, 1920, 1080, () => {
      g.fillStyle = paperTile('#e9e3d3', 41); g.fillRect(0, 0, 1920, 1080);
      const sp = Z().speckle(960, 540, 42, 0.5); g.save(); g.globalCompositeOperation = 'multiply'; g.drawImage(sp, 0, 0, 1920, 1080); g.restore();
      paperRect(960, 1000, 2060, 200, '#8a8578', { seed: 401, torn: true, depth: 1.2, boil: false });
      // the window: frame, dark sky (linocut), mullions
      const W_ = S4.win;
      paperRect(W_.x, W_.y, W_.w + 60, W_.h + 60, '#6b4a2e', { seed: 402, depth: 1.4, boil: false });
      g.fillStyle = '#2e3640'; g.fillRect(W_.x - W_.w / 2, W_.y - W_.h / 2, W_.w, W_.h);
      const r = rng(403); g.strokeStyle = 'rgba(200,210,220,0.18)'; g.lineWidth = 3;
      for (let i = 0; i < 26; i++) { const x = W_.x - W_.w / 2 + r() * W_.w, y = W_.y - W_.h / 2 + r() * W_.h * 0.4; g.beginPath(); g.moveTo(x, y); g.bezierCurveTo(x + 20, y - 12, x + 50, y - 12, x + 70, y); g.stroke(); }
      paperRect(W_.x, W_.y, 20, W_.h, '#6b4a2e', { seed: 404, depth: 0.5, boil: false });
      paperRect(W_.x, W_.y, W_.w, 20, '#6b4a2e', { seed: 405, depth: 0.5, boil: false });
      paperRect(W_.x, W_.y + W_.h / 2 + 34, W_.w + 110, 30, '#7d5a3a', { seed: 406, depth: 1, boil: false });
      // Nia's locker
      const L = S4.locker;
      paperRect(L.x, L.y, L.w, L.h, '#5d7896', { seed: 410, depth: 1.5, boil: false });
      paperRect(L.x, L.y, L.w - 24, L.h - 24, '#6d89a8', { seed: 411, depth: 0.4, boil: false });
      for (let k = 0; k < 5; k++) paperRect(L.x, L.y - L.h / 2 + 60 + k * 18, 150, 7, '#3d5470', { seed: 412 + k, depth: 0, boil: false });
      paperRect(L.x + L.w / 2 - 50, L.y + 20, 22, 90, '#b8c2cc', { seed: 418, depth: 0.8, boil: false });
      g.save(); g.font = "34px 'Special Elite'"; g.fillStyle = WHITE; g.textAlign = 'center'; g.fillText('217', L.x, L.y - L.h / 2 + 170); g.restore();
    }, { zoom: 0.75 });
  }
  function rain(t, k) {
    const W_ = S4.win, n = Math.round(40 + 40 * k);
    g.save(); g.beginPath(); g.rect(W_.x - W_.w / 2, W_.y - W_.h / 2, W_.w, W_.h); g.clip();
    g.strokeStyle = 'rgba(235,240,245,0.62)'; g.lineWidth = 3; g.lineCap = 'round'; g.beginPath();
    for (let i = 0; i < n; i++) {
      const h1 = hash1(i * 7 + 1), h2 = hash1(i * 13 + 5), sp = 700 + 500 * hash1(i * 3 + 2), len = 36 + 40 * hash1(i * 11 + 3);
      const y = ((h2 * (W_.h + 120) + t * sp) % (W_.h + 120)) + W_.y - W_.h / 2 - 60;
      const x = W_.x - W_.w / 2 + ((h1 * W_.w + (y - W_.y) * 0.18) % W_.w + W_.w) % W_.w;
      g.moveTo(x, y); g.lineTo(x - len * 0.18, y - len);
    }
    g.stroke(); g.restore();
  }
  function legDoodle(x, y, t0, xAt) {
    const s = pop(T, t0, 0.3, 1.8); if (s <= 0) return;
    g.save(); g.translate(x, y); g.rotate(-0.08); g.scale(s, s);
    paperRect(0, 0, 200, 190, WHITE, { seed: 421, depth: 1 });
    ink([[-30, -80], [-26, 10], [-34, 60], [10, 70], [52, 66]], { w: 5, color: INK, seed: 422, passes: 1 });
    ink([[10, -80], [8, 10], [6, 44], [40, 46], [56, 56]], { w: 5, color: INK, seed: 423, passes: 1 });
    paperRect(-10, -20, 64, 70, '#f3f0e6', { seed: 424, depth: 0.4, outline: true });
    if (T > xAt) { strike(-80, -80, 80, 80, P(T, xAt, xAt + 0.3), 426, RED, 8); strike(80, -80, -80, 80, P(T, xAt + 0.15, xAt + 0.45), 427, RED, 8); }
    g.restore();
  }
  BODY.s4 = function (t, part, ctx, c) {
    const Zn = Z(), L_ = LINES();
    const pic = L_.picnic || 'Lovely day for a picnic.';
    const tm = {
      sticky: c.W('s4_01', 'break'), threat: c.W('s4_01', 'threat'), luck: c.W('s4_02', 'good'),
      idiom: c.W('s4_03', 'idiom'), whole: c.W('s4_03', 'whole'), inset: c.L('s4_03') - 0.3,
      calls: c.W('s4_04', 'calls'), romeo: c.W('s4_04', 'Romeo'), allu: c.W('s4_04', 'allusion'), back: c.L('s4_05') - 0.2,
      pour: c.W('s4_05', 'pouring'), balloon: c.L('s4_06'),
      hi: branch(c, { q4_right: 'Verbal', q4_wrong0: 'opposite', q4_wrong1: 'verbal' }),
      def: branch(c, { q4_right: 'opposite', q4_wrong0: 'verbal', q4_wrong1: 'opposite' }),
    };
    bgS4();
    rain(t, P(t, tm.pour - 0.2, tm.pour + 0.6));
    // the sticky from a friend on Nia's locker (s4_01) + her reaction doodle
    if (t > tm.sticky) {
      sticky(['BREAK A LEG', 'AT THE SLAM!'], S4.locker.x - 10, 360, tm.sticky, { w: 290, size: 42, seed: 431, rot: -0.06 });
      note('sticky', ['On the locker: "BREAK A LEG AT THE SLAM!"'].concat(t > tm.luck ? ['Break a leg = good luck.'] : []));
    }
    legDoodle(S4.locker.x + 20, 690, tm.threat, tm.threat + 0.6);
    if (t > tm.luck) hand('= good luck', S4.locker.x + 10, 470, 40, tm.luck, { color: '#0f7f70', seed: 432, dur: 0.4, rot: -0.05 });
    // IDIOM and ALLUSION cards (s4_03, s4_04), gone when the rain takes over (s4_05)
    const cardsA = 1 - P(t, tm.back, tm.back + 0.4);
    if (cardsA > 0) alpha(cardsA, () => {
      if (t > tm.idiom - 0.2) {
        const K = S4.idiom;
        indexCard(K.x, K.y, K.w, K.h, pop(t, tm.idiom - 0.2, 0.35, 1.6), { rot: -0.015, seed: 441 }, () => {
          const w = chipW('idiom', 34);
          Zn.label('idiom', -K.w / 2 + 80 + w / 2, -50, { size: 34, rot: -0.03, seed: 442 });
          Zn.type('a phrase you learn whole.', -K.w / 2 + 104 + w, -50, 34, { t0: tm.idiom + 0.2, cps: 45, seed: 443 });
          Zn.type('Break a leg = good luck.', -K.w / 2 + 90, 50, 40, { t0: tm.whole, cps: 40, seed: 444 });
          if (T > tm.whole + 0.5) Zn.highlight(-K.w / 2 + 84, 52, Zn.measure('Break a leg', 40) + 14, 46, COL('idiom'), { t0: tm.whole + 0.5, seed: 445 });
        });
        note('idiom', ['IDIOM: a phrase you learn whole. Break a leg = good luck.']);
      }
      if (t > tm.calls - 0.2) {
        const K = S4.allu;
        indexCard(K.x, K.y, K.w, K.h, pop(t, tm.calls - 0.2, 0.35, 1.6), { rot: 0.012, seed: 451 }, () => {
          Zn.type('a real Romeo', -K.w / 2 + 90, 60, 42, { t0: tm.romeo, cps: 30, seed: 452 });
          if (T > tm.romeo + 0.4) Zn.highlight(-K.w / 2 + 84, 62, Zn.measure('a real Romeo', 42) + 14, 48, COL('allusion'), { t0: tm.romeo + 0.4, seed: 453 });
          if (T > tm.allu) {
            const w = chipW('allusion', 34);
            Zn.label('allusion', -K.w / 2 + 80 + w / 2, -50, { size: 34, rot: 0.02, s: pop(T, tm.allu, 0.3, 1.8), seed: 454 });
            Zn.type('a reference to a known story or person', -K.w / 2 + 104 + w, -50, 28, { t0: tm.allu + 0.2, cps: 50, seed: 455 });
          }
        });
        note('allusion', ['ALLUSION: a reference to a known story or person. Example: "a real Romeo".']);
      }
    });
    // "Lovely day for a picnic." (s4_06) in a speech balloon at the window; red in the reply
    if (t > tm.balloon - 0.1) {
      const B = S4.balloon, s = pop(t, tm.balloon - 0.1, 0.35, 1.8), tw_ = Zn.measure(pic, 48), bw = Math.round(tw_ + 120), bh = 150;
      g.save(); g.translate(B.x, B.y); g.scale(s, s);
      paperPiece(BALLOON_TAIL, 0, 0, WHITE, { seed: 461, depth: 1.2 });
      paperPiece(balloonPts(bw, bh), 0, 0, WHITE, { seed: 462, depth: 1.3, outline: true });
      const geo = { x0: -tw_ / 2, y: 0, size: 48, str: pic };
      Zn.type(pic, geo.x0, 0, 48, { t0: tm.balloon, cps: cpsFor(pic, c, 's4_06') * 1.3, seed: 463 });
      markSub(geo, pic, COL('irony'), tm.hi, { seed: 464, dur: 0.6 });
      g.restore();
      note('picnic', [`Ms. Park: "${pic}" (it's pouring rain)`]);
    }
    if (t > tm.def) {
      const B = S4.balloon;
      Zn.label('irony', B.x - 120, B.y + 150, { size: 36, rot: -0.03, s: pop(t, tm.def, 0.35, 1.8), seed: 471, text: 'VERBAL IRONY' });
      slip('saying the opposite of what you mean', B.x + 20, B.y + 240, 34, tm.def + 0.3, { seed: 472, cps: 50, rot: 0.01 });
      note('irony', ['VERBAL IRONY: saying the opposite of what you mean.']);
    }
    MARKS.s4 = { red: t > tm.hi + 0.6, def: t > tm.def + 0.4 };
    const keys = part.kind === 'lines' ? [[0, 'two'], [tm.inset, 'inset'], [tm.back, 'two']] : [[0, 'two']];
    const st = stage(t, part, keys);
    cast(t, ctx, st);
  };
  /* the balloon: an ellipse-ish blob (cached per size) + a tail toward Ms. Park (right, below) */
  const BAL = new Map();
  function balloonPts(w, h) {
    const k = w + 'x' + h; let p = BAL.get(k);
    if (!p) { p = Object.assign(blobPts(w / 2, h / 2, 463, 0.02, 48), { k: 'simile:balloon:' + k }); BAL.set(k, p); }
    return p;
  }
  const BALLOON_TAIL = Object.assign([[180, 40], [260, 40], [430, 150]], { k: 'simile:balloonTail' });
  S.s4 = {
    timing: { lead: { lines: CARD.lead, reply: 0.4, after: 0.4 }, hold: { lines: 1.4, reply: 1.6, after: 1.8 }, gaps: { s4_06: 'beat' } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: 'pop', t: c.W('s4_01', 'break'), gain: 0.3 }, { name: 'scribble', t: c.W('s4_01', 'threat'), gain: 0.25 }, { name: 'scribble', t: c.W('s4_01', 'threat') + 0.6, gain: 0.25 });
      out.push({ name: 'paper', t: c.W('s4_03', 'idiom') - 0.2, gain: 0.3 }, { name: 'paper', t: c.W('s4_04', 'calls') - 0.2, gain: 0.3 }, { name: 'pop', t: c.W('s4_04', 'allusion'), gain: 0.3 });
      out.push({ name: 'whoosh', t: c.L('s4_05') - 0.2, gain: 0.25 }, { name: 'pop', t: c.L('s4_06') - 0.1, gain: 0.3 });
      out.push({ name: 'scribble', t: branch(c, { q4_right: 'Verbal', q4_wrong0: 'opposite', q4_wrong1: 'verbal' }), gain: 0.3 });
      out.push({ name: 'pop', t: branch(c, { q4_right: 'opposite', q4_wrong0: 'verbal', q4_wrong1: 'opposite' }), gain: 0.3 });
      return out;
    },
  };

  /* ======================================================================
     s5 "Open mic": the stage + spotlight, Dickinson's 1891 lines, the bird, the 1977 card, Nia's zine + PUBLISHED
     ====================================================================== */
  const S5 = { page: { x: 900, y: 420, w: 980, h: 420 }, stage: { x: 960, y: 850, w: 1320 }, mic: { x: 1400, y: 860 }, fact: { x: 610, y: 770 }, zine: { x: 930, y: 440, w: 820, h: 560 } };
  function bgS5() {
    layer('bg_s5', 0, 0, 1920, 1080, () => {
      g.fillStyle = paperTile('#2c2724', 51, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
      const sp = Z().speckle(960, 540, 52, 0.9); g.save(); g.globalCompositeOperation = 'screen'; g.globalAlpha = 0.25; g.drawImage(sp, 0, 0, 1920, 1080); g.restore();
      // curtain folds at the sides
      for (let k = 0; k < 4; k++) { paperRect(40 + k * 60, 470, 70, 1000, k % 2 ? '#6e1f1b' : '#842723', { seed: 501 + k, depth: 0.8, boil: false }); paperRect(1880 - k * 60, 470, 70, 1000, k % 2 ? '#6e1f1b' : '#842723', { seed: 511 + k, depth: 0.8, boil: false }); }
    }, { zoom: 0.75 });
  }
  const SNAPS = [[430, 700, -0.2], [1250, 990, 0.15], [620, 985, 0.1], [1500, 470, -0.12], [960, 1010, 0]];
  function snaps(t, t0) {
    if (t < t0 || t > t0 + 2.6) return;
    const Zn = Z();
    SNAPS.forEach(([x, y, r], i) => {
      const a = t0 + i * 0.18, s = pop(t, a, 0.25, 2.2) * (1 - P(t, a + 1.6, a + 2.0));
      if (s <= 0.01) return;
      g.save(); g.translate(x, y); g.rotate(r); g.scale(s, s);
      Zn.doodle('onomatopoeia', 0, 0, 38, { color: '#f7f5ef', seed: 520 + i, w: 4 });
      Zn.type('snap!', 0, 58, 30, { align: 'center', color: '#f7f5ef', seed: 530 + i });
      g.restore();
    });
  }
  BODY.s5 = function (t, part, ctx, c) {
    const Zn = Z(), L_ = LINES();
    const hope = String(L_.hope || '').split(' / ').filter(Boolean);
    const nq = L_.niaQuestion || "Is a lie still a lie if everyone knows it's a door?";
    const words = ['Hope', 'perches', 'sings', 'never'];
    const tm = {
      page: c.W('s5_01', 'Dickinson'), lines: words.map(w => c.W('s5_02', w)), bird: c.W('s5_03', 'bird'), ext: c.W('s5_04', 'extended'),
      fact: c.W('s5_04', 'study'), zine: c.W('s5_07', 'performing') - 0.2, q: c.W('s5_07', 'Is'), end: c.E('s5_07'),
    };
    bgS5();
    Zn.stage(S5.stage.x, S5.stage.y, S5.stage.w, { t0: CARD.hold, seed: 540 });
    Zn.art('spotlight', 470, S5.stage.y + 4, 220, { seed: 541 });   // on its tripod at stage left, aimed up at the stage
    Zn.art('microphone_stand', S5.mic.x, S5.mic.y, 300, { seed: 542 });
    snaps(t, c.E('s5_02') + 0.3);
    // Dickinson's lines (the 1891 text, facts Q13) type on, one per phrase of s5_02; pink when Nia names the bird
    const pl = track(t, [[0, S5.page.x, S5.page.y, 1, -0.012], [tm.zine, 1560, 300, 0.36, 0.06]], 0.7);
    if (t > tm.page) {
      const Pg = S5.page, s = pop(t, tm.page, 0.4, 1.6) * pl.s;
      g.save(); g.translate(pl.x, pl.y); g.rotate(pl.rot); g.scale(s, s);
      Zn.page(0, 0, Pg.w, Pg.h, { seed: 551, tape: 1, dark: 0.45 });
      Zn.type('"Hope"  Emily Dickinson, Poems, Second Series, 1891', -Pg.w / 2 + 60, -Pg.h / 2 + 50, 26, { color: GREY, seed: 552 });
      const x0 = -Pg.w / 2 + 70;
      hope.forEach((l, i) => {
        const y = -Pg.h / 2 + 120 + i * 66, t0 = tm.lines[i] ?? tm.page, nx = i + 1 < hope.length ? tm.lines[i + 1] : c.E('s5_02');
        Zn.type(l, x0, y, 44, { t0, cps: Math.max(14, l.length / Math.max(0.5, (nx - t0) * 0.9)), seed: 553 + i });
        if (i === 0) markSub({ x0, y, size: 44, str: l }, l, COL('metaphor'), tm.bird, { seed: 560 });
      });
      if (t > tm.bird) Zn.art('bird', Pg.w / 2 - 90, Pg.h / 2 + 60, 190, { seed: 561, s: pop(t, tm.bird, 0.35, 1.8) });
      if (t > tm.ext) Zn.label('metaphor', 60, Pg.h / 2 + 10, { size: 30, rot: -0.03, s: pop(t, tm.ext, 0.35, 1.8), seed: 562, text: 'EXTENDED METAPHOR' });
      g.restore();
      if (t > tm.lines[0]) note('hope', ['"' + hope.filter((l, i) => t > tm.lines[i]).join(' / ') + '" (Emily Dickinson, 1891)'].concat(t > tm.ext ? ['An extended metaphor: hope = a bird.'] : []));
    }
    // the fun fact (s5_04), the facts file's safe wording
    if (t > tm.fact && t < tm.zine + 0.6) alpha(1 - P(t, tm.zine, tm.zine + 0.5), () => {
      indexCard(S5.fact.x, S5.fact.y, 540, 160, pop(t, tm.fact, 0.35, 1.8), { rot: -0.04, seed: 571 }, () => {
        Zn.type('about six figures of speech', -212, -18, 28, { t0: tm.fact + 0.2, cps: 50, seed: 572 });
        Zn.type('a minute, by one 1977 estimate', -212, 32, 28, { t0: tm.fact + 0.8, cps: 50, seed: 573 });
      });
      note('fact', ['About six figures of speech a minute, by one 1977 estimate.']);
    });
    // Nia's zine (s5_07): EXTRA STEPS, the rhetorical question on the cover, PUBLISHED
    if (t > tm.zine) {
      const Zc = S5.zine, s = pop(t, tm.zine, 0.45, 1.5);
      g.save(); g.translate(Zc.x, Zc.y); g.rotate(-0.02); g.scale(s, s);
      Zn.page(0, 0, Zc.w, Zc.h, { seed: 581, staple: 'left', tape: 1, dark: 0.6 });
      ransom('EXTRA STEPS', 20, -Zc.h / 2 + 96, ransomFit('EXTRA STEPS', 582, Zc.w - 160, 90), tm.zine + 0.1, { seed: 582, palette: COVER_PAL });
      Zn.type('by Nia Brooks · issue #1', 20, -Zc.h / 2 + 175, 28, { align: 'center', color: GREY, seed: 583 });
      const ql = Zn.wrap(nq, Zc.w - 170, 44);
      ql.forEach((l, i) => {
        const y = -40 + i * 70, x0 = -Zc.w / 2 + 100, t0 = tm.q + i * (l.length / 30);
        if (T > tm.end + 0.1 + i * 0.3) Zn.highlight(x0 - 6, y + 2, Zn.measure(l, 44) + 12, 50, COL('rhetoricalQuestion'), { t0: tm.end + 0.1 + i * 0.3, dur: 0.4, seed: 590 + i });
        Zn.type(l, x0, y, 44, { t0, cps: 30, seed: 584 + i });
      });
      if (T > tm.end + 0.5) Zn.label('rhetoricalQuestion', -40, 120 + (ql.length - 2) * 70, { size: 32, rot: 0.03, s: pop(T, tm.end + 0.5, 0.35, 1.8), seed: 595 });
      g.restore();
      stampMark('PUBLISHED', Zc.x + 200, Zc.y + 210, stampS(t, tm.end + 1.1) * 0.95, { rot: -0.14, size: 80 });
      MARKS.s5 = { published: t > tm.end + 1.4 };
      note('zine', [`Nia's zine EXTRA STEPS: "${nq}"`].concat(t > tm.end + 0.5 ? ['A rhetorical question: asked for effect, not for an answer.'] : [], t > tm.end + 1.1 ? ['PUBLISHED'] : []));
    }
    snaps(t, tm.end + 1.2);
    const st = stage(t, part, [[0, 'two']]);
    cast(t, ctx, st);
  };
  S.s5 = {
    timing: { lead: { lines: CARD.lead }, hold: { lines: 3.6 }, gaps: { s5_02: 0.8, s5_07: 0.9 } },
    sfx(part, ctx) {
      const c = cues(ctx), out = chapterSfx(part);
      out.push({ name: fx('mic_tap', 'thunk'), t: CARD.hold + 0.8, gain: 0.4 }, { name: 'paper', t: c.W('s5_01', 'Dickinson'), gain: 0.3 });
      ['Hope', 'perches', 'sings', 'never'].forEach(w => out.push({ name: fx('typewriter'), t: c.W('s5_02', w), gain: 0.25 }));
      out.push({ name: fx('snaps', 'sparkle'), t: c.E('s5_02') + 0.3, gain: 0.5 }, { name: 'scribble', t: c.W('s5_03', 'bird'), gain: 0.25 }, { name: 'pop', t: c.W('s5_04', 'extended'), gain: 0.3 });
      out.push({ name: 'paper', t: c.W('s5_04', 'study'), gain: 0.3 }, { name: 'whoosh', t: c.W('s5_07', 'performing') - 0.2, gain: 0.3 });
      const end = c.E('s5_07');
      out.push({ name: 'scribble', t: end + 0.1, gain: 0.25 }, { name: 'pop', t: end + 0.5, gain: 0.3 }, { name: 'stamp', t: end + 1.1, gain: 0.6 }, { name: fx('snaps', 'sparkle'), t: end + 1.2, gain: 0.55 });
      return out;
    },
  };

  /* wrap every scene: the chapter card that tears away to the scene; sfx outside the part are dropped */
  for (const id of Object.keys(BODY)) {
    S[id].draw = function (t, part, ctx) {
      const c = cues(ctx), i = segIndex(part.seg);
      for (const k in NOTES) delete NOTES[k];
      if (part.kind === 'lines' && t < CARD.hold + CARD.tear) {
        if (t < CARD.hold) drawCard(i);
        else tornTransition(() => drawCard(i), u => BODY[id](u, part, ctx, c), t, CARD.hold, CARD.tear, 40 + i);
      } else BODY[id](t, part, ctx, c);
    };
    const sfx = S[id].sfx;
    S[id].sfx = (part, ctx) => sfx(part, ctx).filter(e => e && e.name && e.t > -1e5 && e.t < 1e5);
  }

  /* the placeholder for any segment the script adds later (pure in t): its chapter card + the storyboard note */
  const STAGE = {
    draw(t, part, ctx) {
      Z().cork();
      ransom(String(part.seg.chapter || part.seg.id).toUpperCase(), 960, 110, 64, -1e6, { seed: 31, palette: COVER_PAL });
      const lines = UI._wrap(part.seg.visual || '', 760, `34px ${HANDF}`).slice(0, 6), h = 60 + lines.length * 44;
      Z().page(960, 400, 860, h, { seed: 12, tape: 1 });
      lines.forEach((l, i) => hand(l, 560, 400 - h / 2 + 52 + i * 44, 34, -1e6, { dur: 0.01, align: 'left', color: INK, seed: 20 + i }));
      Puppet.draw('nia', 300, 1060, 600, { env: ctx.speaking('nia'), facing: 1, t });
      Puppet.draw('park', 1620, 1060, 660, { env: ctx.speaking('park'), facing: -1, t });
    },
  };

  window.SimileVideo = { BODY, layers: LY, notes: NOTES, marks: MARKS };

  class VideoScene extends Player.Scene {
    enter() {
      for (const sg of (Game.content.script || { segments: [] }).segments) if (!S[sg.id]) S[sg.id] = STAGE;
      super.enter();
      // bake the backdrops and prints in spare frame time while the first chapter card is up
      if (typeof prewarm === 'function') {
        prewarm.draw(() => bgS3()); prewarm.draw(() => bgS4()); prewarm.draw(() => bgS5());
        prewarm.draw(() => layer('s2_print', -370, -200, 740, 400, balconyPrint));
        prewarm.draw(() => layer('s3_bag', -175, -440, 350, 455, backpackArt));
      }
    }
  }
  Levels.video = VideoScene;
})();
